import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  List,
  ListItem,
  Text,
  HStack,
  useOutsideClick,
} from "@chakra-ui/react";
import { FaChevronDown, FaTimes } from "react-icons/fa";

export function formatVeiculoLabel(v) {
  if (!v) return "";
  const tipo = v.tipo || "Veículo";
  const bocas = v.bocas ? ` (${v.bocas} bocas)` : "";
  return `${v.placa} — ${tipo}${bocas}`;
}

function normalizeSearch(str) {
  return String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

const defaultInputProps = {
  focusBorderColor: "#007abe",
  borderColor: "gray.300",
  bg: "gray.50",
  fontSize: "sm",
  borderRadius: "lg",
  _hover: { borderColor: "#007abe" },
};

const VeiculoAutocomplete = ({
  veiculos = [],
  value = "",
  onChange,
  placeholder = "Digite a placa ou selecione",
  inputProps = {},
}) => {
  const containerRef = useRef(null);
  const [busca, setBusca] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);

  const veiculoSelecionado = useMemo(
    () => veiculos.find((v) => v.id === value) || null,
    [veiculos, value],
  );

  useEffect(() => {
    if (veiculoSelecionado) {
      setBusca(formatVeiculoLabel(veiculoSelecionado));
    } else if (!isOpen) {
      setBusca("");
    }
  }, [veiculoSelecionado, isOpen]);

  const veiculosFiltrados = useMemo(() => {
    const q = normalizeSearch(busca);
    if (!q) return veiculos;
    return veiculos.filter((v) => {
      const placa = normalizeSearch(v.placa);
      const tipo = normalizeSearch(v.tipo);
      const label = normalizeSearch(formatVeiculoLabel(v));
      return placa.includes(q) || tipo.includes(q) || label.includes(q);
    });
  }, [veiculos, busca]);

  useOutsideClick({
    ref: containerRef,
    handler: () => setIsOpen(false),
  });

  const abrirLista = () => {
    setIsOpen(true);
    setHighlightIndex(0);
  };

  const selecionar = (v) => {
    onChange(v.id);
    setBusca(formatVeiculoLabel(v));
    setIsOpen(false);
  };

  const limparSelecao = () => {
    onChange("");
    setBusca("");
    setIsOpen(false);
    setHighlightIndex(0);
  };

  const handleInputChange = (e) => {
    const texto = e.target.value;
    setBusca(texto);
    setIsOpen(true);
    setHighlightIndex(0);

    if (!texto.trim()) {
      onChange("");
      return;
    }

    const q = normalizeSearch(texto);
    const matchExato = veiculos.find(
      (v) => normalizeSearch(v.placa) === q,
    );
    if (matchExato) {
      onChange(matchExato.id);
    } else if (veiculoSelecionado) {
      const labelAtual = formatVeiculoLabel(veiculoSelecionado);
      if (texto !== labelAtual) {
        onChange("");
      }
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
      abrirLista();
      return;
    }
    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) =>
        Math.min(i + 1, Math.max(veiculosFiltrados.length - 1, 0)),
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && veiculosFiltrados[highlightIndex]) {
      e.preventDefault();
      selecionar(veiculosFiltrados[highlightIndex]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const mergedInputProps = { ...defaultInputProps, ...inputProps };
  const temSelecao = Boolean(value);

  return (
    <Box ref={containerRef} position="relative">
      <InputGroup>
        <Input
          {...mergedInputProps}
          value={busca}
          onChange={handleInputChange}
          onFocus={abrirLista}
          onClick={abrirLista}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          pr={temSelecao ? "4.25rem" : "2.5rem"}
          autoComplete="off"
        />
        <InputRightElement width="auto" h="100%" pr={1}>
          <HStack spacing={0}>
            {temSelecao && (
              <IconButton
                aria-label="Limpar veículo selecionado"
                size="sm"
                variant="ghost"
                color="gray.500"
                _hover={{ color: "red.500", bg: "red.50" }}
                icon={<FaTimes />}
                onMouseDown={(e) => e.preventDefault()}
                onClick={limparSelecao}
              />
            )}
            <IconButton
              aria-label="Abrir lista de veículos"
              size="sm"
              variant="ghost"
              color="gray.500"
              icon={<FaChevronDown />}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => (isOpen ? setIsOpen(false) : abrirLista())}
            />
          </HStack>
        </InputRightElement>
      </InputGroup>

      {isOpen && (
        <List
          position="absolute"
          zIndex={20}
          top="100%"
          left={0}
          right={0}
          mt={1}
          maxH="220px"
          overflowY="auto"
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="md"
          py={1}
        >
          {veiculosFiltrados.length === 0 ? (
            <ListItem px={3} py={2}>
              <Text fontSize="sm" color="gray.500">
                Nenhum veículo encontrado
              </Text>
            </ListItem>
          ) : (
            veiculosFiltrados.map((v, index) => (
              <ListItem
                key={v.id}
                px={3}
                py={2}
                fontSize="sm"
                cursor="pointer"
                bg={index === highlightIndex ? "blue.50" : "transparent"}
                _hover={{ bg: "blue.50" }}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlightIndex(index)}
                onClick={() => selecionar(v)}
              >
                <Text fontWeight="semibold" color="gray.800">
                  {v.placa}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {v.tipo || "Veículo"}
                  {v.bocas ? ` · ${v.bocas} bocas` : ""}
                </Text>
              </ListItem>
            ))
          )}
        </List>
      )}
    </Box>
  );
};

export default VeiculoAutocomplete;
